import { Dispatch } from 'application/redux'
import { Calendar } from 'assets/svg'
import TimeInput from 'components/Input/components/Calendar/TimeInput'
import CleanField from 'components/Input/components/CleanField'
import { I18nContext } from 'contexts/I18n/I18nProvider'
import { addToaster } from 'core/toaster/redux/actions'
import { ToasterType } from 'core/toaster/redux/types'
import dayjs from 'dayjs'
import { useContext, useState } from 'react'
import { useDispatch } from 'react-redux'
import CalendarComponent from '../../components/Calendar/Calendar/Index'
import CalendarModal from '../../components/Calendar/Modal'
import { DatePickerProps, DatepickerValue } from '../../types'
import { validSelectedDateAndTime } from './helper'
import { END_TIME_OBJECT, START_TIME_OBJECT, TimeObject } from './types'


function getTime(dateString: string, fallbackValue: TimeObject) {
  if (!dateString) {
    return fallbackValue
  }

  const date = new Date(dateString)

  return {
    hours: date.getHours(),
    minutes: date.getMinutes(),
  }
}

function buildDateTimeString(date: string, time: TimeObject) {
  return dayjs(new Date(`${date} ${time.hours}:${time.minutes}`)).format('YYYY-MM-DD HH:mm:ss')
}

function renderInputLabel(value: DatepickerValue, fallbackLabel?: string) {
  const t = useContext(I18nContext)

  if (value.start && value.end) {
    return `${t('general.from')} ${dayjs(value.start).format('YYYY-MM-DD HH:mm:ss')} ${t(
      'general.to'
    )} ${dayjs(value.end).format('YYYY-MM-DD HH:mm:ss')}`;
  }

  return t(fallbackLabel || 'general.select_date')
}

export default function DatepickerComponent({ value, setValue, labelParams, calendarYearRange = 1 }: DatePickerProps) {
  const [open, setOpen] = useState(false)

  const [startDate, setStartDate] = useState(dayjs(value.start || Date.now()).format('YYYY-MM-DD'))
  const [endDate, setEndDate] = useState(dayjs(value.end || Date.now()).format('YYYY-MM-DD'))

  const [firstValue, setFirstValue] = useState('')
  const [secondValue, setSecondValue] = useState('')

  const [startTime, setStartTime] = useState<TimeObject>(getTime(value.start, START_TIME_OBJECT))
  const [endTime, setEndTime] = useState<TimeObject>(getTime(value.end, END_TIME_OBJECT))

  const t = useContext(I18nContext)
  const dispatch = useDispatch<Dispatch>()

  const showModal = () => {
    if (value.start && value.end) {
      setStartDate(dayjs(value.start || Date.now()).format('YYYY-MM-DD'))
      setEndDate(dayjs(value.end || Date.now()).format('YYYY-MM-DD'))
      setStartTime(getTime(value.start, START_TIME_OBJECT))
      setEndTime(getTime(value.end, END_TIME_OBJECT))
      setFirstValue(dayjs(value.start).format('YYYY-MM-DD'))
      setSecondValue(dayjs(value.end).format('YYYY-MM-DD'))
    }

    setOpen(true)
  }

  const handleOk = () => {
    if(validSelectedDateAndTime(startDate, startTime, endDate, endTime)){
      setValue({
        start: buildDateTimeString(startDate, startTime),
        end: buildDateTimeString(endDate, endTime),
      })
      return setOpen(false)

    } 
    dispatch(
      addToaster({
        title: t('general.calendar'),
        message: t('error_messages.VIPOC_ERROR_070'),
        type: ToasterType.danger
      })
    )
  }

  const handleCancel = () => {
    setStartDate(dayjs(value.start || Date.now()).format('YYYY-MM-DD'))
    setEndDate(dayjs(value.end || Date.now()).format('YYYY-MM-DD'))
    setStartTime(getTime(value.start, START_TIME_OBJECT))
    setEndTime(getTime(value.end, END_TIME_OBJECT))
    setOpen(false)
  }

  return (
    <>
      <div className="date-input-container">
        <div className="field">
          {value.start && value.end && labelParams?.onClean &&
            <span className="datepicker-floating-label">
              <span className="has-padding-right-xs">
                {t(labelParams?.i18nTag || 'general.select_date')}
              </span>
              <span className="has-padding-top-xxs input-wrap-tooltip">
                <CleanField onClean={labelParams.onClean} />
              </span>
            </span>
          }
          <button className="button button-date-label" type="button">
            <div onClick={showModal}>
              <span>{renderInputLabel(value, labelParams?.i18nTag)}</span>
              <Calendar className="icon icon-svg" onClick={showModal} />
            </div>
          </button>
        </div>
      </div>
      <CalendarModal
        isOpen={open}
        handleCancel={handleCancel}
        handleConfirm={handleOk}
        footer={<TimeInput
          startTime={startTime}
          setStartTime={setStartTime}
          endTime={endTime}
          setEndTime={setEndTime}
          handleOnOk={handleOk}
          handleOncancel={handleCancel}
        />}
      >
        <CalendarComponent
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          firstValue={firstValue}
          secondValue={secondValue}
          setFirstValue={setFirstValue}
          setSecondValue={setSecondValue}
          calendarYearRange={calendarYearRange}
        />
      </CalendarModal>
    </>
  )
}
